import { Badge, Card, CardContent } from "@exhousing/design-system";
import { toast } from "sonner";

import { useTenants, useUpdateTenant } from "@/hooks/api/useTenants";

const PLANS = ["free", "starter", "professional", "standard"];

const SELECT =
  "h-8 rounded-md border border-input bg-background px-2 text-sm outline-none focus:border-primary";

function formatDate(value: string): string {
  return new Date(value).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

export function TenantsPage() {
  const { data: tenants } = useTenants();
  const update = useUpdateTenant();

  return (
    <div className="space-y-6">
      <header>
        <h1 className="font-display text-3xl font-medium tracking-tight">Agencies</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {tenants ? `${tenants.length} agencies on the platform` : "Loading…"}
        </p>
      </header>

      <Card>
        <CardContent className="overflow-x-auto p-0">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
                <th className="px-6 py-3">Agency</th>
                <th className="px-6 py-3">Plan</th>
                <th className="px-6 py-3 text-right">Users</th>
                <th className="px-6 py-3 text-right">Listings</th>
                <th className="px-6 py-3">Joined</th>
                <th className="px-6 py-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {tenants?.map((t) => (
                <tr key={t.id} className="border-b border-border/60 last:border-0">
                  <td className="px-6 py-3">
                    <p className="font-medium">{t.name}</p>
                    <p className="text-xs text-muted-foreground">{t.slug}</p>
                  </td>
                  <td className="px-6 py-3">
                    <select
                      className={SELECT}
                      value={t.subscription_tier}
                      onChange={(e) =>
                        update.mutate(
                          { id: t.id, subscription_tier: e.target.value },
                          {
                            onSuccess: () => toast.success(`${t.name} plan updated`),
                            onError: () => toast.error("Could not update plan"),
                          },
                        )
                      }
                    >
                      {PLANS.map((p) => (
                        <option key={p} value={p}>
                          {p}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="px-6 py-3 text-right tabular-nums">{t.users}</td>
                  <td className="px-6 py-3 text-right tabular-nums">
                    {t.published_listings}
                  </td>
                  <td className="px-6 py-3 text-muted-foreground">
                    {formatDate(t.created_at)}
                  </td>
                  <td className="px-6 py-3">
                    <button
                      type="button"
                      onClick={() =>
                        update.mutate(
                          { id: t.id, is_active: !t.is_active },
                          {
                            onSuccess: () =>
                              toast.success(
                                t.is_active
                                  ? `${t.name} suspended`
                                  : `${t.name} reactivated`,
                              ),
                            onError: () => toast.error("Could not update status"),
                          },
                        )
                      }
                      title={t.is_active ? "Click to suspend" : "Click to reactivate"}
                    >
                      <Badge tone={t.is_active ? "success" : "danger"} dot>
                        {t.is_active ? "Active" : "Suspended"}
                      </Badge>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
